import type { InviteViewModel } from "@bundle/core";
import { Avatar, Button, Icon, SignInForm, Stack, Text, useSession, useT, useViewModel } from "@bundle/ui";
import { useEffect } from "react";
import { AuthCard } from "./SignInScreen.tsx";

export type InviteScreenProps = {
  vm: InviteViewModel;
  /** Where an OAuth redirect comes back to (this invite page). */
  returnTo: string;
};

/** /invite/<token>: who invited you where; sign in if needed, then join (the session then opens the workspace). */
export function InviteScreen({ vm, returnTo }: InviteScreenProps) {
  const t = useT();
  const state = useViewModel(vm);
  const session = useSession();
  const signedIn = session.state.status === "signedIn";

  useEffect(() => {
    void vm.load();
  }, [vm]);

  if (state.status === "invalid") {
    return (
      <AuthCard testID="invite-screen">
        <Stack gap="1">
          <Text variant="title3" weight="bold">
            {t("invite.accept.invalid")}
          </Text>
          <Text color="text.secondary">{t("invite.accept.invalidBody")}</Text>
        </Stack>
        <Button label={t("invite.accept.continue")} variant="primary" onPress={() => vm.dismiss()} testID="invite-continue" />
      </AuthCard>
    );
  }

  const preview = state.preview;
  const join = () => void vm.join();

  return (
    <AuthCard testID="invite-screen">
      {preview === undefined ? (
        state.status === "error" ? (
          <Stack gap="3">
            <Text color="text.secondary">{t("invite.accept.offline")}</Text>
            <Button label={t("common.retry")} icon="reload" onPress={() => void vm.load()} testID="invite-retry" />
          </Stack>
        ) : (
          <Text color="text.secondary">{t("common.loading")}</Text>
        )
      ) : (
        <Stack gap="3">
          <Stack direction="horizontal" align="center">
            <Avatar name={preview.workspace.name} src={preview.workspace.iconUrl} size="avatar.xl" square />
          </Stack>
          <Text variant="title2" weight="bold" testID="invite-title">
            {preview.inviterName === ""
              ? t("invite.accept.titleAnonymous", { workspace: preview.workspace.name })
              : t("invite.accept.title", { inviter: preview.inviterName, workspace: preview.workspace.name })}
          </Text>
          <Text color="text.secondary">{t("invite.accept.subtitle")}</Text>
        </Stack>
      )}
      {state.error === undefined ? null : (
        <Stack direction="horizontal" gap="2" align="start" testID="invite-error">
          <Icon name={state.error === "network" ? "offline" : "alert"} size="icon.sm" color="status.danger" />
          <Text variant="footnote" color="status.danger">
            {state.error === "emailMismatch" ? t("invite.accept.emailMismatch") : state.error === "network" ? t("invite.accept.offline") : t("common.error")}
          </Text>
        </Stack>
      )}
      {signedIn ? (
        <Stack gap="3">
          <Button
            label={t("invite.accept.join", { workspace: preview?.workspace.name ?? "" })}
            variant="primary"
            size="lg"
            loading={state.joining}
            disabled={preview === undefined}
            onPress={join}
            testID="invite-join"
          />
          <Stack direction="horizontal" justify="between" align="center" wrap>
            <Text variant="footnote" color="text.tertiary">
              {t("auth.signedInAs", { email: session.state.session?.email ?? "" })}
            </Text>
            <Button label={t("auth.switchAccount")} variant="ghost" size="sm" onPress={() => session.vm.signOut()} testID="invite-switch-account" />
          </Stack>
        </Stack>
      ) : (
        <Stack gap="3">
          <Text variant="headline">{t("invite.accept.signInToJoin")}</Text>
          <SignInForm returnTo={returnTo} />
        </Stack>
      )}
    </AuthCard>
  );
}
